<script setup lang="ts">
import { nanoid } from 'nanoid'

const props = defineProps({
  modelValue: {
    type: String,
    required: false,
  },
  selectOptions: {
    type: Array as PropType<SelectOption[]>,
    required: true,
  },
  label: {
    type: String,
    required: true,
  },
  errorMessage: {
    type: String,
    required: false,
  },
  hint: {
    type: String,
    required: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  clearable: {
    type: Boolean,
    default: false,
  },
  prependIcon: {
    type: String,
    required: false,
  },
})

const emit = defineEmits({
  'update:modelValue': (_value?: string) => true,
  'validate': () => true,
})

function handleUpdate(selectedOption?: string) {
  const newValue = !selectedOption ? undefined : selectedOption

  if (newValue !== props.modelValue) {
    emit('update:modelValue', newValue)
    emit('validate')
  }
}

const id = nanoid()
</script>

<template>
  <div class="w-full">
    <InputGroup>
      <InputGroupAddon v-if="props.prependIcon">
        <i :class="[props.prependIcon, 'pi', 'ml-2', 'mr-2']" />
      </InputGroupAddon>
      <FloatLabel variant="in">
        <Select
          v-bind-once="{ 'input-id': id }"
          :model-value="props.modelValue"
          class="h-[50px]"
          :show-clear="props.clearable"
          :disabled="props.disabled"
          :invalid="!!props.errorMessage"
          :options="props.selectOptions"
          option-label="label"
          option-value="value"
          @update:model-value="handleUpdate"
        />
        <label v-bind-once="{ for: id }">{{ props.label }}</label>
      </FloatLabel>
    </InputGroup>
    <span
      v-if="props.hint"
      :class="['block', props.errorMessage ? 'text-red-600' : 'text-slate-500', 'text-xs', 'mt-1', 'ml-3', 'mr-3']"
    >{{ props.hint }}</span>
    <span
      v-if="props.errorMessage"
      class="block text-red-600 text-xs mt-1 ml-3 mr-3"
    >{{ props.errorMessage }}</span>
  </div>
</template>
